//AdminSide
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import axiosInstance from './utils/axiosInstance';
import Navbar from './components/Navbar/Navbar';
import Dashboard from './components/dashboard/dashboard';
import Sidebar from './components/Sidebar/sidebar';
import Table from './components/EmployeeStatus/Table';
import WorkHistory from './components/EmployeeHistory/EmployeeHistory';
import ManageEmployee from './components/manageEmployees/manageEmployee';
import Reports from './components/Reports/Reports';
import EmployeeStats from './components/EmployeeStats/EmployeeStats';
import Login from './components/Login/Login';
import LeaveRequests from './components/LeaveRequests/LeaveRequests';
import AttendanceRequests from './components/attendanceRequests/attendanceRequests';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import 'react-tooltip/dist/react-tooltip.css'
import ApprovalsPage from './components/approvalPage/approvalPage';
import ManageHolidays from './components/ManageHolidays/ManageHolidays';
import ManageProject from './components/manageProjects/manageProjects';
import EmployeeWorkingData from './components/manageProjects/projectDetails/EmployeeDetails/employeeWorkingData/employeeWorkingData';
import SessionTimeout from './components/SessionTimeOut/SessionTimeOut';
import RegistrationRequests from './components/manageEmployees/RegistrationRequests/RegistrationRequests';
import RequestDetails from './components/manageEmployees/RegistrationRequests/RequestDetails';
import { toast } from 'react-toastify';
import { useMediaQuery } from 'react-responsive';
import CollapsibleSideNavbar from './components/Sidebar/CollapsibleSideNavbar';
import ManageProjectsByEmployees from './components/ManageProjectsByEmployees/ManageProjectsByEmployees';
import ProjectDetailsByEmployee from './components/ManageProjectsByEmployees/ProjectDetailsByEmployee/ProjectDetailsByEmployee';
import LLM from './components/LLMPage/LLM';
import SalesDashboard from './components/SalesDashboard/SalesDashboard';
import EmployeeHierarchy from './components/EmployeeHierarchy/EmployeeHierarchy';

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 min timeout if no activity
const TOKEN_TIMEOUT = 1 * 60 * 60 * 1000; // 1 hour

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [employeeIds, setEmployeeIds] = useState([]);
  const [loggedInEmployeeId, setLoggedInEmployeeId] = useState(null);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [empRole, setEmpRole] = useState('');
  const [companyId, setCompanyId] = useState();
  const inactivityTimer = useRef(null);
  const tokenExpirationTimer = useRef(null);
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  // Define handlers first using useCallback
  const handleSessionTimeout = useCallback(() => {
    // Clear all timers first
    clearTimeout(inactivityTimer.current);
    clearTimeout(tokenExpirationTimer.current);

    setIsSessionExpired(true);
    localStorage.setItem('sessionExpired', 'true');

    // Clear all session-related data
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('loggedInEmployee');
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('tokenExpiration');
    localStorage.removeItem('activeTab');

    // Use replace instead of href to prevent adding to history
    window.location.replace('/session-timeout');
  }, []);

  const resetInactivityTimer = useCallback(() => {
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      handleSessionTimeout();
    }, INACTIVITY_TIMEOUT);

    // Update last activity timestamp
    const now = Date.now();
    localStorage.setItem('lastActivity', now.toString());
  }, [handleSessionTimeout]);

  const resetTokenExpirationTimer = useCallback(() => {
    clearTimeout(tokenExpirationTimer.current);
    tokenExpirationTimer.current = setTimeout(() => {
      handleSessionTimeout();
    }, TOKEN_TIMEOUT);

    // Update token expiration timestamp
    const now = Date.now();
    localStorage.setItem('tokenExpiration', (now + TOKEN_TIMEOUT).toString());
  }, [handleSessionTimeout]);

  const setupInactivityListeners = useCallback(() => {
    // Only set up listeners if user is authenticated and not on public routes
    if (!isAuthenticated ||
      window.location.pathname === '/session-timeout' ||
      window.location.pathname === '/') {
      return () => { };
    }

    const events = ['mousemove', 'keypress', 'click', 'touchstart', 'scroll'];

    const handleActivity = () => {
      resetInactivityTimer();
    };

    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, resetInactivityTimer]);

  const checkSessionStatus = useCallback(() => {
    // Don't check session if user is not authenticated or is on public routes
    if (!isAuthenticated ||
      window.location.pathname === '/session-timeout' ||
      window.location.pathname === '/') {
      return;
    }

    const sessionExpired = localStorage.getItem('sessionExpired') === 'true';
    const token = localStorage.getItem('token');
    const lastActivity = localStorage.getItem('lastActivity');
    const now = Date.now();

    if (sessionExpired || !token) {
      handleSessionTimeout();
      return;
    }

    if (lastActivity && (now - parseInt(lastActivity)) > INACTIVITY_TIMEOUT) {
      handleSessionTimeout();
      return;
    }

    const tokenExpiration = localStorage.getItem('tokenExpiration');
    if (tokenExpiration && now > parseInt(tokenExpiration)) {
      handleSessionTimeout();
      return;
    }
  }, [isAuthenticated, handleSessionTimeout]);

  const handleLogin = useCallback((token, refreshToken, user) => {
    setIsAuthenticated(true);
    setLoggedInEmployeeId(user.emp_id);
    setCompanyId(user.company_id);
    // console.log("user id -", user.emp_id)
    // console.log("company id -", user.company_id)

    const now = Date.now();
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('loggedInEmployee', JSON.stringify(user));
    localStorage.setItem('lastActivity', now.toString());
    localStorage.setItem('tokenExpiration', (now + TOKEN_TIMEOUT).toString());
    localStorage.removeItem('sessionExpired');

    resetInactivityTimer();
    resetTokenExpirationTimer();
  }, [resetInactivityTimer, resetTokenExpirationTimer]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('activeTab');
    localStorage.removeItem('loggedInEmployee');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('sessionExpired');
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('tokenExpiration');
    localStorage.removeItem('loggedInEmployeeId');
    localStorage.removeItem('companyId');
    localStorage.removeItem('employeeIds');

    setLoggedInEmployeeId(null);
    setIsAuthenticated(false);
    setIsSessionExpired(false);

    clearTimeout(inactivityTimer.current);
    clearTimeout(tokenExpirationTimer.current);

    toast.success('Logged out successfully');
  }, []);

  // Effects
  // Modified session check interval effect
  useEffect(() => {
    let intervalId;

    // Only start interval if user is authenticated and not on public routes
    if (isAuthenticated &&
      window.location.pathname !== '/session-timeout' &&
      window.location.pathname !== '/') {
      intervalId = setInterval(checkSessionStatus, 60000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isAuthenticated, checkSessionStatus]);

  // Update the initial authentication effect
  useEffect(() => {
    const loggedInEmployee = localStorage.getItem('loggedInEmployee');
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    const sessionExpired = localStorage.getItem('sessionExpired') === 'true';

    if (sessionExpired) {
      setIsSessionExpired(true);
    } else if (loggedInEmployee && token && refreshToken) {
      try {
        const employee = JSON.parse(loggedInEmployee);
        setLoggedInEmployeeId(employee.emp_id);
        setIsAuthenticated(true);
        // Only start timers if not on public routes
        if (window.location.pathname !== '/session-timeout' &&
          window.location.pathname !== '/') {
          resetInactivityTimer();
          resetTokenExpirationTimer();
        }
      } catch (error) {
        console.error('Error parsing loggedInEmployee from localStorage', error);
      }
    }
    setIsLoading(false);
  }, [resetInactivityTimer, resetTokenExpirationTimer]);

  useEffect(() => {
    const intervalId = setInterval(checkSessionStatus, 60000); // Check every minute
    return () => clearInterval(intervalId);
  }, [checkSessionStatus]);

  useEffect(() => {
    const fetchEmployeeInfo = async () => {
      const storedEmployeeId = localStorage.getItem('loggedInEmployeeId');
      const storedCompanyId = localStorage.getItem('companyId');
      const storedEmpRole = localStorage.getItem('empRole');
      setEmpRole(storedEmpRole);

      if (storedEmployeeId) {
        try {
          const response = await axiosInstance.get('/admin/allemployeesInfo');
          const data = response.data;
          if (storedEmpRole === 'SuperAdmin') {
            setEmployeeIds(data.map(employee => employee.emp_id));
          } else {
            const filteredData = data.filter(employee => employee.company_id === parseInt(storedCompanyId));
            // Filter data to get employees under the logged-in admin's hierarchy
            const buildEmployeeHierarchy = (managerId, employees) => {
              const managerIdNum = Number(managerId);
              const directReports = employees.filter(emp => Number(emp.manager_id) === managerIdNum);
              const allSubordinates = [...directReports];

              directReports.forEach(report => {
                const subordinates = buildEmployeeHierarchy(Number(report.emp_id), employees);
                allSubordinates.push(...subordinates);
              });

              return allSubordinates;
            };

            // Get all employees under the logged-in admin's hierarchy
            const hierarchicalEmployees = buildEmployeeHierarchy(Number(storedEmployeeId), filteredData);
            const ids = hierarchicalEmployees.map(employee => Number(employee.emp_id)).filter(n => Number.isFinite(n));
            setEmployeeIds(ids);
            localStorage.setItem('employeeIds', JSON.stringify(ids)); // Persist employeeIds
          }

        } catch (error) {
          console.error('Error fetching employee info:', error.message);
        }
      }
    };

    fetchEmployeeInfo();
  }, [loggedInEmployeeId]);


  // Render logic
  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isSessionExpired) {
    return (
      <Router>
        <SessionTimeout handleLogout={handleLogout} />
      </Router>
    );
  }

  return (
    <Router>
      <div className='page-container'>
        {!isAuthenticated ? (
          <div className='main'>
            <Routes>
              <Route path='*' element={<Login handleLogin={handleLogin} />} />
            </Routes>
          </div>
        ) : (
          <>
            <div className='sidebar'>
              <>
                {isMobile ? (
                  <CollapsibleSideNavbar handleLogout={handleLogout} employeeId={loggedInEmployeeId} />
                ) : (
                  <Sidebar handleLogout={handleLogout} employeeId={loggedInEmployeeId} />
                )}
              </>
            </div>
            <div className='main'>
              <ConditionalNavbar employeeId={loggedInEmployeeId} />
              <Routes>
                <Route exact path='/' element={<Dashboard adminIds={employeeIds} empRole={empRole} />} />
                <Route path='/EmployeeOperations' element={<Table adminIds={employeeIds} empRole={empRole} />} />
                <Route path='/projects-by-employee/:employeeId' element={<ManageProjectsByEmployees />} />
                <Route path='/employee-project-details/:employeeId' element={<ProjectDetailsByEmployee />} />
                <Route path='/WorkHistory' element={<WorkHistory adminIds={employeeIds} />} />
                <Route path='/ManageEmployees' element={<ManageEmployee adminIds={employeeIds} loggedInEmployeeId={loggedInEmployeeId} empRole={empRole} />} />
                <Route path='/ManageHolidays' element={<ManageHolidays adminIds={employeeIds} loggedInEmployeeId={loggedInEmployeeId} />} />
                <Route path='/Reports' element={<Reports adminIds={employeeIds} empRole={empRole} />} />
                <Route path='/EmployeeStats' element={<EmployeeStats adminIds={employeeIds} />} />
                <Route path='/ApprovalsPage' element={<ApprovalsPage adminIds={employeeIds} empRole={empRole} />} />
                <Route path='/LeaveRequests' element={<LeaveRequests adminIds={employeeIds} empRole={empRole} />} />
                <Route path='/AttendanceRequests' element={<AttendanceRequests adminIds={employeeIds} empRole={empRole} />} />
                <Route path='/manageProjects' element={<ManageProject adminIds={employeeIds} loggedInEmployeeId={loggedInEmployeeId} empRole={empRole} />} />
                <Route path='/employee-hierarchy' element={<EmployeeHierarchy />} />
                <Route path="/employee/:employeeName" element={<EmployeeWorkingData />} />
                <Route path="/registrationRequests" element={<RegistrationRequests loggedInEmployeeId={loggedInEmployeeId} empRole={empRole} companyId={companyId} />} />
                <Route path="/request-details/:id" element={<RequestDetails />} />
                <Route path="/llm-model" element={<LLM />} />
                <Route path="/sales-dashboard" element={<SalesDashboard />} />
                <Route path='*' element={<Dashboard adminIds={employeeIds} empRole={empRole} />} />
              </Routes>
            </div>
          </>
        )}
      </div>
    </Router>
  );
};

const ConditionalNavbar = ({ employeeId }) => {
  const location = useLocation();
  return location.pathname === '/' ? <Navbar employeeId={employeeId} /> : null;
};

export default App;
